import { useState } from 'react';
import { Link } from 'react-router-dom';
import { newsList } from '@/mocks/news';
import { movies } from '@/mocks/movies';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 4;

export default function News() {
  const [page, setPage] = useState(1);

  usePageMeta(
    '影视资讯 - 青苹果影院',
    '青苹果影院影视资讯栏目，整理最新影视动态、剧集观察、幕后故事与片单推荐，帮助你了解正在发生的影视话题。',
    '影视资讯,影视动态,剧集观察,幕后故事,片单推荐,青苹果影院',
  );

  const totalPages = Math.max(1, Math.ceil(newsList.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageNews = newsList.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const hotMovies = [...movies].sort((a, b) => b.popularity - a.popularity).slice(0, 5);

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    window.scrollTo({ top: 220, behavior: 'smooth' });
  };

  return (
    <div className="w-full">
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 h-11 flex items-center gap-2 text-xs text-foreground-500">
          <Link to="/" className="hover:text-primary-700 transition-colors">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm" aria-hidden="true" />
          <span className="text-foreground-800">影视资讯</span>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto px-4 md:px-8 pt-8 md:pt-10 pb-14 md:pb-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500" aria-hidden="true" />
              <span className="text-xs tracking-[0.18em] text-foreground-500 uppercase font-label">Journal</span>
            </div>
            <h1 className="mt-2 text-2xl md:text-3xl font-semibold text-foreground-950 tracking-tight">影视资讯</h1>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed max-w-2xl">
              影视动态、剧集观察、幕后故事与片单推荐。我们用更慢的方式，聊一部作品为什么值得看。
            </p>
          </div>
          <span className="text-xs text-foreground-400 font-label">共 {newsList.length} 篇 · 第 {currentPage} / {totalPages} 页</span>
        </div>

        <div className="mt-6 md:mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          <div className="lg:col-span-8">
            {pageNews.length > 0 ? (
              <div className="space-y-4 md:space-y-5">
                {pageNews.map((item, index) => (
                  <article
                    key={item.id}
                    className="rounded-lg border border-background-200 bg-background-50 overflow-hidden"
                  >
                    <Link
                      to={`/news/${item.id}`}
                      className="group flex flex-col sm:flex-row gap-4 sm:gap-5 p-4 md:p-5"
                    >
                      <div className="relative w-full sm:w-[200px] md:w-[220px] shrink-0 aspect-[16/10] sm:aspect-[4/3] rounded-md overflow-hidden bg-background-200">
                        <img
                          src={item.cover}
                          alt={item.title}
                          title={`${item.title} - 影视资讯`}
                          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.03]"
                        />
                        {index === 0 && currentPage === 1 && (
                          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-accent-500 text-foreground-950 text-[11px] font-medium">
                            最新
                          </span>
                        )}
                      </div>

                      <div className="min-w-0 flex-1 flex flex-col">
                        <div className="flex items-center gap-2 text-[11px] text-foreground-500">
                          <span className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-800">影视观察</span>
                          <span className="font-label">{item.date}</span>
                          <span className="hidden sm:inline">·</span>
                          <span className="hidden sm:inline truncate">{item.author}</span>
                        </div>
                        <h2 className="mt-2 text-base md:text-lg font-semibold text-foreground-950 leading-snug group-hover:text-primary-700 transition-colors">
                          {item.title}
                        </h2>
                        <p className="mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2">
                          {item.summary}
                        </p>
                        <span className="mt-3 inline-flex items-center gap-1 text-xs md:text-sm text-primary-700 font-medium">
                          阅读全文
                          <i className="ri-arrow-right-line" aria-hidden="true" />
                        </span>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            ) : (
              <EmptyState
                icon="ri-newspaper-line"
                title="暂无资讯内容"
                description="资讯正在整理中，可以先到影片分类页浏览作品。"
                actionLabel="浏览影片"
                actionTo="/category"
              />
            )}

            {newsList.length > 0 && (
              <div className="mt-8 md:mt-10">
                <Pagination page={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
              </div>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-foreground-950">热门影片</h2>
                <Link to="/category?tag=hot" className="text-xs text-primary-700 hover:text-primary-800">
                  全部
                </Link>
              </div>
              <ul className="mt-4 space-y-3">
                {hotMovies.map((movie, index) => (
                  <li key={movie.id}>
                    <Link to={`/detail/${movie.id}`} className="group flex items-center gap-3">
                      <span className="w-5 text-center font-label text-base font-semibold text-background-400 group-hover:text-primary-500 transition-colors">
                        {index + 1}
                      </span>
                      <span className="w-10 h-14 shrink-0 rounded-md overflow-hidden bg-background-200">
                        <img
                          src={movie.poster}
                          alt={`${movie.title}海报`}
                          title={`${movie.title} - 影视详情`}
                          className="w-full h-full object-cover object-top"
                        />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-foreground-900 truncate group-hover:text-primary-700 transition-colors">
                          {movie.title}
                        </span>
                        <span className="mt-0.5 block text-[11px] text-foreground-500 truncate">
                          {movie.category === 'movie' ? '电影' : '电视剧'} · {movie.year}
                        </span>
                      </span>
                      <span className="text-xs font-label font-semibold text-accent-700">{movie.rating.toFixed(1)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-4 rounded-lg bg-primary-50 border border-primary-100 p-4 md:p-5">
              <div className="flex items-center gap-2 text-primary-800">
                <i className="ri-lightbulb-line text-lg" aria-hidden="true" />
                <span className="text-sm font-medium">阅读提示</span>
              </div>
              <p className="mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed">
                资讯内容为本站编辑整理的演示文稿，用于展示页面结构与阅读体验，不代表任何真实影视项目。
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}